<script>
import { mapActions } from 'vuex'
import { mask } from 'vue-the-mask'
import amountMask from '@lib/utils/amount-mask'
import ValidationMixin from '@lib/mixins/validation'

export default {
  name: 'ClaimCreate',
  directives: {
    mask,
  },
  page: {
    title: 'Settle a Claim',
  },
  mixins: [ValidationMixin],
  data: function() {
    return {
      progress: false,
      form: {},
      showErrorMessage: false,
      rules: {
        number: [
          {
            required: true,
            message: 'This field is required',
          },
          {
            pattern: /^[a-zA-Z0-9]*$/,
            message: 'Claim ID must only contain letters and numbers',
            trigger: ['blur', 'change'],
          },
          {
            max: 10,
            message: 'Claim ID must be 10 characters or less',
            trigger: ['blur', 'change'],
          },
        ],
        firstName: {
          required: true,
          message: 'This field is required',
        },
        lastName: {
          required: true,
          message: 'This field is required',
        },
        email: [
          {
            required: true,
            message: 'This field is required',
          },
          {
            type: 'email',
            message: 'Must be a valid email address',
            trigger: ['blur', 'change'],
          },
        ],
        itce: [
          {
            required: true,
            message: 'This field is required',
          },
          {
            validator: (rule, value, callback) => {
              const valueFloat = parseFloat(value)
              if (valueFloat < 0 || valueFloat > 100) {
                callback(new Error('ITCE must be between 0% and 100%'))
              } else {
                callback()
              }
            },
          },
        ],
        amount: {
          required: true,
          message: 'This field is required',
        },
      },
    }
  },
  methods: {
    ...mapActions('claims', ['createClaim']),
    amountMask,
    onChange(field, value) {
      this.form[field] = value
      delete this.backendErrors[field]
      this.$refs.form.validateField(field)
    },
    onCancel() {
      this.$router.push({ name: 'claims' })
    },
    async onSubmit() {
      this.clearBackendErrors()
      const formValid = await this.$refs.form.validate().catch(() => false)
      if (!formValid) return

      this.form.itce = parseFloat(this.form.itce)
      this.form.amount = parseFloat(this.form.amount)

      this.progress = true

      const response = await this.createClaim(this.form)

      this.progress = false

      const [error] = response

      if (error) {
        this.processBackendErrors(error)

        const { detail } = error

        if (detail === 'Access Denied.') {
          this.showErrorMessage = true
        }
      } else {
        this.$notifier({
          response,
          successMsg: `Claim ${this.form.number} successfully created. An email containing a personalised URL and login details has been sent to the member.`,
        }).then(this.onCancel)
      }
    },
    removeLeadingZero(value) {
      this.form.itce = !value || value[0] !== '0' ? value : value.replace(/^0+(?!$)/, '')
    },
  },
}
</script>

<template>
  <main-layout
    title="Back to Claims"
    :back="{ name: 'claims' }"
  >
    <data-box
      header="New Claim Settlement"
      :status="null"
    >
      <el-form
        ref="form"
        :model="form"
        :rules="rules"
        label-position="left"
        class="card-form"
      >
        <el-form-item
          label="Claim No."
          prop="number"
          :error="backendErrors.number"
          :class="$style.formItem"
        >
          <el-input
            v-model="form.number"
            @input="onChange('number', $event)"
          />
        </el-form-item>
        <el-form-item
          label="Member's First Name"
          prop="firstName"
          :error="backendErrors.firstName"
          :class="$style.formItem"
        >
          <el-input v-model="form.firstName" />
        </el-form-item>
        <el-form-item
          label="Member's Last Name"
          prop="lastName"
          :error="backendErrors.lastName"
          :class="$style.formItem"
        >
          <el-input v-model="form.lastName" />
        </el-form-item>
        <el-form-item
          label="Member's Email"
          prop="email"
          :error="backendErrors.email"
          :class="$style.formItem"
        >
          <el-input v-model="form.email" />
        </el-form-item>
        <el-form-item
          label="ITCE"
          prop="itce"
          :error="backendErrors.itce"
          :class="$style.formItem"
        >
          <el-input
            v-model="form.itce"
            v-mask="'###'"
            :class="$style.inputShort"
            @input="removeLeadingZero"
          >
            <template #append>
              %
            </template>
          </el-input>
        </el-form-item>
        <el-form-item
          label="Claim Settlement Amount"
          prop="amount"
          :error="backendErrors.amount"
          :class="$style.formItem"
        >
          <el-input
            v-model="form.amount"
            @input="onChange('amount', amountMask($event))"
          >
            <template #prepend>
              $
            </template>
          </el-input>
        </el-form-item>
      </el-form>
      <div :class="$style.formText">
        Once saved, an email will be sent out to the member.
      </div>
      <div>
        <el-button
          type="primary"
          plain
          :class="$style.button"
          @click="onCancel"
        >
          Cancel
        </el-button>
        <el-button
          type="primary"
          :class="$style.button"
          :loading="progress"
          @click="onSubmit"
        >
          Save
        </el-button>
      </div>

      <div
        v-if="showErrorMessage"
        :class="$style.errorMessage"
      >
        This record cannot be saved. This claim may exceed the value you are authorised to settle or edit.
      </div>
    </data-box>
  </main-layout>
</template>

<style lang="scss" module>
.formItem {
  label {
    margin-top: rem(8px);
  }
}

.inputShort {
  max-width: rem(140px);
}

.formText {
  margin: rem(30px 0 20px);
}

.button {
  padding-right: rem(50px);
  padding-left: rem(50px);
}

.errorMessage {
  margin-top: 1rem;
  color: var(--color-error);
}
</style>
